import { executeGraphQL } from '../../../src/graphql/executeGraphQL';
import type { GraphQLRequestOverrides, GraphqlDocumentOperation } from '../../../src/graphql/executeGraphQL';
import type { GqlGetTemplateVersionSelectionQuery, GqlGetTemplateVersionSelectionQueryVariables } from './GetTemplateVersionSelection';
import type { GqlIndustryTemplatesQuery, GqlIndustryTemplatesQueryVariables } from './IndustryTemplates';
import type { GqlMyTemplatesQuery, GqlMyTemplatesQueryVariables } from './MyTemplates';
import type { GqlResetTemplateDraftMutation, GqlResetTemplateDraftMutationVariables } from './ResetTemplateDraft';
import type { GqlSharedTemplatesQuery, GqlSharedTemplatesQueryVariables } from './SharedTemplates';
import type { GqlTemplateLibraryQuery, GqlTemplateLibraryQueryVariables } from './TemplateLibrary';

const GetTemplateVersionSelectionOperation: GraphqlDocumentOperation = {
  name: 'GetTemplateVersionSelection',
  type: 'query',
  document: `query GetTemplateVersionSelection($templateId: ID!, $versionId: ID!) {
  templateContainer(id: $templateId) {
    id
    version(versionId: $versionId) {
      id
      number
    }
    productionVersion {
      id
    }
    draft {
      id
    }
  }
}`,
};

const IndustryTemplatesOperation: GraphqlDocumentOperation = {
  name: 'IndustryTemplates',
  type: 'query',
  document: `query IndustryTemplates {
  industryTemplates {
    id
    name
    isArchived
    effectivePermissions {
      write
    }
    productionVersion {
      number
    }
    draft {
      id
    }
  }
}`,
};

const MyTemplatesOperation: GraphqlDocumentOperation = {
  name: 'MyTemplates',
  type: 'query',
  document: `query MyTemplates($scopedToWorkbookId: ID!) {
  myTemplates(scopedToWorkbookId: $scopedToWorkbookId) {
    id
    name
    isArchived
    effectivePermissions {
      write
    }
    productionVersion {
      number
    }
    draft {
      id
    }
  }
}`,
};

const ResetTemplateDraftOperation: GraphqlDocumentOperation = {
  name: 'ResetTemplateDraft',
  type: 'mutation',
  document: `mutation ResetTemplateDraft($input: ResetTemplateDraftInput!) {
  resetTemplateDraft(input: $input) {
    id
    sourceVersion {
      id
      number
    }
    template {
      id
      name
      description
      scopedTo
      inputFeatures {
        id
        canonicalId
        name
        description
        outputType
        isRequired
        outputItemType
        outputItemMetadata
      }
      derivedFeatures {
        id
        canonicalId
        name
        description
        outputType
        isOutput
        outputItemType
        outputItemMetadata
        rules {
          __typename
          id
          inputs {
            ...TemplateRuleInputFields
          }
          ... on TemplateFormulaCreatorRule {
            formula {
              ...TemplateRuleInputFields
            }
            parameters {
              ...TemplateRuleInputFields
            }
            name {
              ...TemplateRuleInputFields
            }
            description {
              ...TemplateRuleInputFields
            }
            scopedTo {
              ...TemplateRuleInputFields
            }
            identityId {
              ...TemplateRuleInputFields
            }
            permissionString {
              ...TemplateRuleInputFields
            }
            variableParameters {
              ...TemplateRuleInputFields
            }
            variableParameterStrings {
              ...TemplateRuleInputFields
            }
          }
          ... on TemplateStringScalarCreatorRule {
            name {
              ...TemplateRuleInputFields
            }
            description {
              ...TemplateRuleInputFields
            }
            scopedTo {
              ...TemplateRuleInputFields
            }
            identityId {
              ...TemplateRuleInputFields
            }
            permissionString {
              ...TemplateRuleInputFields
            }
          }
          ... on TemplateNumericScalarCreatorRule {
            name {
              ...TemplateRuleInputFields
            }
            description {
              ...TemplateRuleInputFields
            }
            scopedTo {
              ...TemplateRuleInputFields
            }
            identityId {
              ...TemplateRuleInputFields
            }
            permissionString {
              ...TemplateRuleInputFields
            }
          }
          ... on TemplateBooleanConstantRule {
            staticValue
          }
        }
      }
    }
  }
}

fragment TemplateRuleInputFields on TemplateRuleInput {
  id
  name
  description
  type
  inputFeatureId
  argument
}`,
};

const SharedTemplatesOperation: GraphqlDocumentOperation = {
  name: 'SharedTemplates',
  type: 'query',
  document: `query SharedTemplates($scopedToWorkbookId: ID!) {
  sharedTemplates(scopedToWorkbookId: $scopedToWorkbookId) {
    id
    name
    isArchived
    effectivePermissions {
      write
    }
    productionVersion {
      number
    }
    draft {
      id
    }
  }
}`,
};

const TemplateLibraryOperation: GraphqlDocumentOperation = {
  name: 'TemplateLibrary',
  type: 'query',
  document: `query TemplateLibrary($filter: TemplateSearchFilterInput, $limit: Int) {
  templates(filter: $filter, limit: $limit) {
    totalResults
    items {
      id
      name
      effectivePermissions {
        write
      }
      productionVersion {
        number
      }
      draft {
        id
      }
    }
  }
}`,
};

export class sqTemplatesGqlApiClass {
  /** GraphQL query GetTemplateVersionSelection (src/graphql/operations/templates/GetTemplateVersionSelection.graphql) */
  public getTemplateVersionSelection(
    variables: GqlGetTemplateVersionSelectionQueryVariables,
    overrides?: GraphQLRequestOverrides,
  ): Promise<GqlGetTemplateVersionSelectionQuery> {
    return executeGraphQL(GetTemplateVersionSelectionOperation, variables, overrides);
  }

  /** GraphQL query IndustryTemplates (src/graphql/operations/templates/IndustryTemplates.graphql) */
  public industryTemplates(
    variables?: GqlIndustryTemplatesQueryVariables,
    overrides?: GraphQLRequestOverrides,
  ): Promise<GqlIndustryTemplatesQuery> {
    return executeGraphQL(IndustryTemplatesOperation, variables, overrides);
  }

  /** GraphQL query MyTemplates (src/graphql/operations/templates/MyTemplates.graphql) */
  public myTemplates(
    variables: GqlMyTemplatesQueryVariables,
    overrides?: GraphQLRequestOverrides,
  ): Promise<GqlMyTemplatesQuery> {
    return executeGraphQL(MyTemplatesOperation, variables, overrides);
  }

  /** GraphQL mutation ResetTemplateDraft (src/graphql/operations/templates/ResetTemplateDraft.graphql) */
  public resetTemplateDraft(
    variables: GqlResetTemplateDraftMutationVariables,
    overrides?: GraphQLRequestOverrides,
  ): Promise<GqlResetTemplateDraftMutation> {
    return executeGraphQL(ResetTemplateDraftOperation, variables, overrides);
  }

  /** GraphQL query SharedTemplates (src/graphql/operations/templates/SharedTemplates.graphql) */
  public sharedTemplates(
    variables: GqlSharedTemplatesQueryVariables,
    overrides?: GraphQLRequestOverrides,
  ): Promise<GqlSharedTemplatesQuery> {
    return executeGraphQL(SharedTemplatesOperation, variables, overrides);
  }

  /** GraphQL query TemplateLibrary (src/graphql/operations/templates/TemplateLibrary.graphql) */
  public templateLibrary(
    variables?: GqlTemplateLibraryQueryVariables,
    overrides?: GraphQLRequestOverrides,
  ): Promise<GqlTemplateLibraryQuery> {
    return executeGraphQL(TemplateLibraryOperation, variables, overrides);
  }
}

export const sqTemplatesGqlApi = new sqTemplatesGqlApiClass();
